import { formatInTimeZone } from 'date-fns-tz';
import type { TFunction } from 'i18next';

export const formatUTC = (date: Date | string, formatStr: string) => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return formatInTimeZone(dateObj, 'UTC', formatStr);
};

export const formatPercentage = (basisPoints: number) => {
  const percentage = basisPoints / 100;
  return `${percentage.toFixed(2)}%`;
};

export const formatCurrency = (amountInCents: number) => {
  return (amountInCents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

export const getCurrentBillingWeek = () => {
  const now = new Date();
  const utcDay = now.getUTCDay();
  const daysToMonday = (utcDay + 6) % 7;
  const weekStart = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() - daysToMonday,
    0, 0, 0, 0
  ));
  const weekEnd = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate() + (6 - utcDay),
    23, 59, 0, 0
  ));
  const formatDate = (d: Date) =>
    d.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'UTC'
    }).replace(',', '');
  return `${formatDate(weekStart)} - ${formatDate(weekEnd)} UTC`;
};

export const getPaymentMethodIconType = (type: string): 'card' | 'bank' | 'link' | 'wallet' => {
  switch (type) {
    case 'card':
      return 'card';
    case 'us_bank_account':
    case 'sepa_debit':
    case 'bacs_debit':
    case 'acss_debit':
      return 'bank';
    case 'link':
      return 'link';
    case 'paypal':
      return 'wallet';
    default:
      return 'card';
  }
};

export const formatPaymentMethodText = (pm: any, t: TFunction) => {
  if (pm.card) {
    const brand = pm.card.brand?.toUpperCase() || 'CARD';
    const funding = pm.card.funding ? ` (${pm.card.funding})` : '';
    const expiration = t('paymentMethods.labels.expires', {
      month: String(pm.card.expMonth).padStart(2, '0'),
      year: pm.card.expYear,
    });
    const country = pm.card.country ? ` • ${pm.card.country}` : '';

    return {
      primary: `${brand} •••• ${pm.card.last4}${funding}`,
      secondary: `${expiration}${country}`,
      tertiary: pm.holderName || pm.holderEmail || pm.billingDetails?.name || pm.billingDetails?.email || null,
    };
  }

  // Provider-neutral summaries (`billing.listPaymentMethods`) carry a one-line
  // label for non-card methods instead of the Stripe-specific sub-objects.
  if (pm.label) {
    return {
      primary: pm.label,
      secondary: pm.type.replace('_', ' ').toUpperCase(),
      tertiary: pm.holderName || pm.holderEmail || null,
    };
  }

  if (pm.usBankAccount) {
    const bankName = pm.usBankAccount.bankName || t('paymentMethods.labels.bankAccount');
    const accountType = pm.usBankAccount.accountType === 'checking'
      ? t('paymentMethods.labels.checking')
      : t('paymentMethods.labels.savings');
    const business = pm.usBankAccount.accountHolderType === 'company'
      ? ` ${t('paymentMethods.labels.business')}`
      : '';

    return {
      primary: `${bankName} •••• ${pm.usBankAccount.last4}`,
      secondary: t('paymentMethods.labels.accountSuffix', { type: accountType, business }),
      tertiary: pm.billingDetails?.name || pm.billingDetails?.email || null,
    };
  }

  if (pm.sepaDebit) {
    const country = pm.sepaDebit.country?.toUpperCase() || '';

    return {
      primary: `${t('paymentMethods.labels.sepaDebit')} •••• ${pm.sepaDebit.last4}`,
      secondary: t('paymentMethods.labels.sepaCountry', { country }),
      tertiary: pm.billingDetails?.name || pm.billingDetails?.email || null,
    };
  }

  if (pm.link) {
    return {
      primary: t('paymentMethods.labels.stripeLink'),
      secondary: pm.link.email,
      tertiary: t('paymentMethods.labels.linkTertiary'),
    };
  }

  if (pm.paypal) {
    return {
      primary: t('paymentMethods.labels.paypal'),
      secondary: pm.paypal.payerEmail,
      tertiary: pm.paypal.payerId ? t('paymentMethods.labels.paypalId', { id: pm.paypal.payerId }) : null,
    };
  }

  return {
    primary: pm.type.replace('_', ' ').toUpperCase(),
    secondary: t('paymentMethods.labels.unknownFallback'),
    tertiary: null,
  };
};

export const isPaymentMethodExpired = (pm: any): boolean => {
  if (!pm.card) return false;

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  if (pm.card.expYear < currentYear) return true;
  if (pm.card.expYear === currentYear && pm.card.expMonth < currentMonth) return true;

  return false;
};

export const isPaymentMethodExpiringSoon = (pm: any): boolean => {
  if (!pm.card || isPaymentMethodExpired(pm)) return false;

  const now = new Date();
  const expiryDate = new Date(pm.card.expYear, pm.card.expMonth - 1);
  const twoMonthsFromNow = new Date();
  twoMonthsFromNow.setMonth(twoMonthsFromNow.getMonth() + 2);

  return expiryDate <= twoMonthsFromNow;
};

export const getPriceDescriptionKey = (
  itemId: string,
  customFee?: { isRecurring?: boolean },
) => {
  if (customFee?.isRecurring === true) return 'pricing.priceDescriptions.perWeek';
  if (customFee?.isRecurring === false) return 'pricing.priceDescriptions.oneTime';
  if (itemId === 'apple-pay-domain' || itemId === 'custom-domain') return 'pricing.priceDescriptions.perDomainPerWeek';
  return 'pricing.priceDescriptions.perTransaction';
};

export const getPercentageDescriptionKey = (itemId: string) => {
  if (itemId === 'auto-retry') return 'pricing.percentageDescriptions.recoveredVolume';
  if (itemId === 'upsell') return 'pricing.percentageDescriptions.orderBumpVolume';
  if (itemId === 'subscription') return 'pricing.percentageDescriptions.subscriptionVolume';
  if (itemId === 'apple-pay' || itemId === 'google-pay') return 'pricing.percentageDescriptions.walletVolume';
  return 'pricing.percentageDescriptions.totalVolume';
};
